// usePickerTaskLock
// -----------------
// The picker equivalent of useTaskLock, but SO-scoped (option (b): a picker works
// one Sales Order at a time). Loads the active picker task for a request + the SO
// the picker opened from the Pickers List (?picker_so) and exposes:
//   - needsAssign  — the SO is unassigned; the picker must claim it (take-on gate)
//   - assignedToOther — the assignee's name when it is held by ANOTHER picker
//                       (a peer is blocked from opening it; a supervisor+ may view)
//   - handleAssign — claim the SO's task for the current user
//
// null soNumber / not a picker → no-op (loading false, no gate): the scopeless
// fallback shows the whole request, matching the picker view's own fallback.

import React, { useState } from "react";
import { findPickerLockTask, updateTaskById } from "../data/requestTasks";

interface PickerTaskRow {
  id: string | number;
  assigned_to?: string | null;
  assigned_to_id?: string | null;
  completed_at?: string | null;
}
interface PickerLockUser {
  id?: string | null;
  shelterName?: string | null;
}

export function usePickerTaskLock(
  requestId: number | string | null | undefined,
  soNumber: string | null | undefined,
  isPicker: boolean,
  user: PickerLockUser
) {
  const [task, setTask] = useState<PickerTaskRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsAssign, setNeedsAssign] = useState(false);
  const [assignedToOther, setAssignedToOther] = useState<string | null>(null);
  const [takingOn, setTakingOn] = useState(false);

  React.useEffect(
    function () {
      if (!isPicker || !requestId || !soNumber) {
        setLoading(false);
        setNeedsAssign(false);
        setAssignedToOther(null);
        setTask(null);
        return;
      }
      findPickerLockTask(requestId, soNumber).then(function (t) {
        setLoading(false);
        if (!t) {
          setNeedsAssign(false);
          setAssignedToOther(null);
          setTask(null);
          return;
        }
        setTask(t);
        if (t.assigned_to) {
          const mine =
            (t.assigned_to_id && t.assigned_to_id === user.id) ||
            t.assigned_to === user.shelterName;
          setNeedsAssign(false);
          setAssignedToOther(mine ? null : t.assigned_to);
        } else {
          setNeedsAssign(true);
          setAssignedToOther(null);
        }
      });
    },
    [requestId, soNumber, isPicker]
  );

  async function handleAssign() {
    if (!task?.id) return;
    setTakingOn(true);
    const now = new Date().toISOString();
    const { error } = await updateTaskById(task.id, {
      assigned_to: user.shelterName,
      assigned_to_id: user.id,
      assigned_at: now,
    });
    if (!error) {
      setNeedsAssign(false);
      setAssignedToOther(null);
    }
    setTakingOn(false);
  }

  return { loading, needsAssign, assignedToOther, takingOn, handleAssign, task };
}
